import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';
import { listChatSessions } from 'shared/services/chatService';
import { formatSessionTime, groupSessionsByRecency, SESSION_GROUP_LABEL_CLASS } from 'features/dashboard/chatSessions';

/**
 * Search every conversation you have had, from the rail.
 *
 * The rail lists ten. That is quick access, not history — the whole list was
 * being loaded to render a sidebar, which got slower with every conversation
 * and still made you scroll to find anything. Everything past the ten lives
 * here, where a query is a query rather than a scroll.
 *
 * Searching asks the server (`GET /chat/sessions?q=`), so it reaches
 * conversations the rail never loaded. Empty query shows the most recent.
 */
export default function ChatSearchDialog({ open, teamId, onPick, onClose }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) { setQ(''); setRows(null); return undefined; }
    const t = setTimeout(() => inputRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [open]);

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    if (!open || !teamId) return undefined;
    let cancelled = false;
    const t = setTimeout(() => {
      listChatSessions(teamId, { limit: 50, offset: 0, ...(q.trim() ? { q: q.trim() } : {}) })
        .then((r) => { if (!cancelled) setRows(r?.sessions || []); })
        .catch(() => { if (!cancelled) setRows([]); });
    }, q.trim() ? 180 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, teamId, q]);

  const onKeyDown = useCallback((e) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onKeyDown]);

  if (!open) return null;

  const groups = groupSessionsByRecency(rows || []);

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-start justify-center p-4 pt-[12vh]">
      <div className="absolute inset-0 bg-[color:var(--surface-overlay)]" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search chats"
        className="relative flex max-h-[70vh] w-full max-w-[520px] flex-col overflow-hidden rounded-xl border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-token-lg"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <div className="flex items-center gap-2.5 border-b border-[color:var(--border-default)] px-3.5 py-3">
          <Search className="h-4 w-4 shrink-0 text-[color:var(--text-muted)]" aria-hidden />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search your chats…"
            className="flex-1 bg-transparent text-[13.5px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-1">
          {rows === null && (
            <p className="px-4 py-6 text-center text-[12.5px] text-[color:var(--text-muted)]">Loading…</p>
          )}
          {rows && rows.length === 0 && (
            <p className="px-4 py-6 text-center text-[12.5px] text-[color:var(--text-muted)]">
              {q.trim() ? `Nothing matches “${q.trim()}”` : 'No conversations yet'}
            </p>
          )}
          {groups.map(({ label, items }) => (
            <div key={label}>
              <p className={`${SESSION_GROUP_LABEL_CLASS} !px-4`}>
                {label}
              </p>
              {items.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => { onPick(s.id); onClose(); }}
                  className="flex w-full items-center gap-3 px-4 py-1.5 text-left hover:bg-surface-hover"
                >
                  <span className="min-w-0 flex-1 truncate text-[13px] text-[color:var(--text-primary)]">
                    {s.title || 'Untitled conversation'}
                  </span>
                  <span className="shrink-0 text-[12px] tabular-nums text-[color:var(--text-muted)]">
                    {formatSessionTime(s.updatedAt)}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
